<template>
  <component
    :is="tag"
    v-bind="attr"
    :class="['badge ', mode, size, { active, 'has-active': isHasActiveClass }]"
    v-on="$listeners"
  >
    <slot>
      <span class="badge-text">
        {{ text }}
      </span>
    </slot>
    <slot name="close" />
    <template v-if="isSpin">
      <div class="badge-spin">
        <svg
          class="badge-spin-svg"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    </template>
  </component>
</template>

<script>
export default {
  name: 'AppBadge',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    isSpin: {
      type: Boolean,
      default: false
    },
    tag: {
      type: String,
      default: 'div'
    },
    to: {
      type: [String, Object],
      default: ''
    },
    text: {
      type: String,
      default: 'نام درس'
    },
    size: {
      type: String,
      default: 'medium'
    },
    mode: {
      type: String,
      default: 'primary'
    },
    isHasActiveClass: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    attr () {
      if (this.tag === 'nuxt-link') {
        return { to: this.to }
      }
      if (this.tag === 'a') {
        return { href: this.to, target: '_blank' }
      }
      return ''
    }
  }
}
</script>
<style lang="scss">
.badge {
  @apply rounded-3xl justify-center  inline-flex cursor-pointer items-center relative;
  &.large {
    @apply py-2 h-8 px-3;
    span {
      @apply text-xs;
    }
  }

  &.medium {
    @apply py-1 max-h-8 px-3;
    span {
      @apply text-xs;
    }
  }

  &.small {
    @apply py-2 max-h-[24px] px-3;
    span {
      @apply text-xs-xs xs:text-xs;
    }
  }

  span {
    @apply whitespace-nowrap font-normal;
  }

  &.primary {
    @apply bg-blue-very-light hover:bg-blue-primary;
    span {
      color: theme("colors.gray.medium");
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.secondary-primary {
    @apply bg-white hover:bg-blue-primary transition-colors;
    span {
      @apply text-gray-medium transition-colors;
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.secondary-primary-background {
    @apply bg-white md:bg-blue-background hover:bg-blue-primary transition-colors;
    span {
      @apply text-gray-medium transition-colors;
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.secondary-blue {
    @apply bg-blue-very-light hover:bg-blue-primary;
    span {
      @apply text-blue-primary;
    }

    &:hover {
      span,
      svg {
        @apply text-white;
      }
    }
  }

  &.light {
    @apply bg-white hover:bg-blue-primary border border-gray-disabled;
    span {
      color: theme("colors.gray.medium");
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.light-blue {
    @apply bg-blue-light hover:bg-blue-primary;
    span {
      color: theme("colors.gray.medium");
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.transparent {
    @apply bg-white hover:bg-blue-primary border border-gray-disabled;
    span {
      color: theme("colors.gray.medium");
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.transparent-primary {
    @apply relative z-10 overflow-hidden;
    &:before {
      content: "";
      @apply absolute inset-0 bg-blue-primary  scale-0 transition-transform -z-1
    }

    span {
      color: theme("colors.gray.medium");
    }

    &:hover {
      &:before {
        @apply scale-100;
      }

      span {
        @apply text-white;
      }
    }
  }

  &.primary-outline {
    @apply bg-white hover:bg-blue-primary border border-gray-medium md:border-blue-primary;
    span {
      @apply text-gray-medium md:text-blue-primary;
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.text {
    @apply bg-transparent hover:bg-blue-light;
    span {
      @apply text-gray-new-main;
    }

    &:hover {
      span {
        @apply text-white;
      }
    }
  }

  &.link-light {
    @apply bg-transparent hover:bg-white transition-colors;
    span {
      @apply text-white transition-colors;
    }

    &:hover {
      span {
        @apply text-blue-primary;
      }
    }

    &.has-active.active,
    &.has-active.nuxt-link-active {
      @apply bg-white;
      span {
        @apply text-blue-primary;
      }
    }
  }

  &.has-active.active:not(.link-light),
  &.has-active.nuxt-link-active:not(.link-light) {
    @apply bg-blue-primary hover:bg-blue-primary;
    span {
      @apply text-white;
    }
  }

  &-spin {
    @apply absolute inset-0 m-auto  flex place-items-center justify-center bg-white opacity-70 pointer-events-none z-20;
    .badge-spin-svg {
      @apply animate-spin h-4 w-4;
    }
  }
}
</style>
